import React from 'react';
import { ReadingTiles, SOFT_CARD_SHADOW, TargetBar } from '../../controlPanel/MobilePourRow';
import type { PourRowTarget, PourRowTile } from '../../controlPanel/pourCardModel';
import { Box, Skeleton, Typography } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CloudOff as CloudOffIcon,
  Event as EventIcon,
  HourglassEmpty as HourglassIcon,
  InfoOutlined as InfoIcon,
  Sensors as SensorsIcon,
  StopCircle as StopCircleIcon,
  SwapVert as SwapVertIcon,
  Thermostat as ThermostatIcon,
  TrackChanges as TargetIcon,
  Warning as WarningIcon,
  WbSunny as SunIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { MaturityDescriptor, Pour } from '../../../services/pours';
import { formatMaturity } from '../../../utils/maturityDisplay';
import { getOnlineStatus, pourStatusEnded } from '../../../utils/onlineStatus';
import {
  formatAgo,
  formatDuration,
  formatIn,
  pourClockStart,
  type PourClockStart,
  type TranslateFn,
} from '../../../utils/relativeTime';
import { useNow } from '../../../utils/useNow';
import { FRESHNESS_TINTS } from '../locationDisplay';
import type { PourMaturitySummary, PourPhase, TargetCount, WorstDifferential } from './mobileDashboardModel';
import { freshnessWord, limitNumber, shortDateTime, toEpochMs } from './mobileText';
import { PILL_TONES, type PillTone } from './StatusPill';

/** The strip's counts: the dashboard's pourCounts, or the Control Panel
 *  row's while the dashboard loads (ok and reporting unknown there). */
export interface StripCounts {
  outsideLimits: number;
  /** Some location has limits: without, the outside-limits chip is hidden
   *  and the unchecked locations read "N reporting" rather than "N without
   *  limits". */
  hasLimits: boolean;
  notReporting: number;
  delayed: number;
  sensor: number;
  /** Within their limits ("N OK"); null when unknown. */
  ok: number | null;
  /** Reporting with no limit checked (no limit set, site and humidity
   *  channels); null when unknown. */
  reporting: number | null;
  waiting: number;
}

/** The site's weather now (evaporation and weather pours). The summary
 *  sends no evaporation limit value (only each location's over flag), so
 *  the line says "Over limit" in words; the Location tab names the limit. */
export interface StripSite {
  temp: number | null;
  humidity: number | null;
  /** The highest current evaporation rate of the concrete locations. */
  evaporation: number | null;
  evaporationOver: boolean;
}

export interface PourStatusStripProps {
  phase: PourPhase;
  /** What "since start" counts from (pourClockStart). */
  clock: PourClockStart | null;
  startsAt: number | null;
  endedAt: number | null;
  newestReadingAt: number | null;
  counts: StripCounts | null;
  /** The time of the data (the last successful fetch): the newest
   *  reading's freshness is judged against it. */
  refNow: number;
  timezoneIana?: string | null;
  /** Mass concrete: the worst differential against its limit. */
  worstDiff?: WorstDifferential | null;
  /** Maturity: the pour's next target. */
  maturity?: PourMaturitySummary | null;
  meta?: MaturityDescriptor;
  site?: StripSite | null;
  /** Painted from the Control Panel row while the dashboard loads. */
  fromList?: boolean;
  /** The labelled readings (Concrete max, Ambient, Differential,
   *  Strength): when given they replace the differential, maturity and
   *  site lines. */
  tiles?: PourRowTile[] | null;
  /** The bar to the lowest location's next target. */
  target?: PourRowTarget | null;
  /** How far into its planned monitoring ("day 3 of 7", or "36 d"): the
   *  monitoring line then reads "Monitoring · Day 3 of 7" with the start and
   *  the newest reading on one line under it. */
  progress?: string | null;
  /** How many locations the pour monitors: a grey chip after the problem
   *  chips. */
  locationCount?: number | null;
  /** Locations over the evaporation limit now (not counted as outside
   *  limits). */
  evaporationOver?: number;
  /** Maturity: every target with how many locations reached it. */
  targetCounts?: TargetCount[] | null;
}

/** The soft card the Overview's status and locations sit in. */
export const SOFT_CARD_SX = {
  bgcolor: 'background.paper',
  borderRadius: '18px',
  boxShadow: SOFT_CARD_SHADOW,
} as const;

const LINE_SX = { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere' } as const;

/** "since concrete start", by which start the clock counts from. */
const sinceText = (clock: PourClockStart, now: number, t: TranslateFn): string => {
  const duration = formatDuration(now - clock.at, t);
  switch (clock.kind) {
    case 'concrete':
      return t('mobile.dashboard.strip.sinceConcrete', { defaultValue: '{{duration}} since concrete start', duration });
    case 'monitoring':
      return t('mobile.dashboard.strip.sinceMonitoring', { defaultValue: '{{duration}} since monitoring start', duration });
    case 'planned':
      // The Control Panel card's word for the same start.
      return t('mobile.dashboard.strip.sinceLogger', { defaultValue: '{{duration}} since logger start', duration });
    default:
      return t('mobile.dashboard.strip.sincePour', { defaultValue: '{{duration}} since pour start', duration });
  }
};

/** Which start the clock counts from, as words for the status line. */
const START_WORDS: Record<string, string> = {
  concrete: 'Concrete start',
  monitoring: 'Monitoring start',
  planned: 'Logger start',
  pour: 'Pour start',
};

/** One count: colour, icon and word, at least 32px high. */
const CountChip: React.FC<{ tone: PillTone; Icon: React.ComponentType<SvgIconProps>; text: string }> = ({
  tone,
  Icon,
  text,
}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      minHeight: 32,
      px: 1.25,
      borderRadius: '999px',
      bgcolor: PILL_TONES[tone].bg,
      color: PILL_TONES[tone].fg,
      fontSize: '0.875rem',
      fontWeight: 600,
    }}
  >
    <Icon aria-hidden sx={{ fontSize: 18 }} />
    {text}
  </Box>
);

/** One line of the strip: an icon and its text. */
const StripLine: React.FC<{
  Icon: React.ComponentType<SvgIconProps>;
  color?: string;
  testId?: string;
  children: React.ReactNode;
}> = ({ Icon, color, testId, children }) => (
  <Box data-testid={testId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: color ?? 'text.primary', minWidth: 0 }}>
    <Icon aria-hidden sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }} />
    <Typography component="div" sx={{ ...LINE_SX, color: 'inherit' }}>
      {children}
    </Typography>
  </Box>
);

/**
 * The Overview's pour status strip: the phase with the time since the pour
 * started (saying which start), the count chips, the newest reading's age,
 * and per function the worst differential, the next maturity target and
 * the site weather. Relative times stay live.
 */
export const PourStatusStrip: React.FC<PourStatusStripProps> = ({
  phase,
  clock,
  startsAt,
  endedAt,
  newestReadingAt,
  counts,
  refNow,
  timezoneIana,
  worstDiff,
  maturity,
  meta,
  site,
  fromList = false,
  tiles,
  target,
  progress,
  locationCount,
  evaporationOver = 0,
  targetCounts,
}) => {
  const { t } = useTranslation();
  const now = useNow();

  let phaseIcon: React.ComponentType<SvgIconProps> = SensorsIcon;
  let phaseText: string;
  let phaseDetail = '';
  switch (phase) {
    case 'no_locations':
      phaseIcon = InfoIcon;
      phaseText = t('mobile.dashboard.strip.noLocations', { defaultValue: 'No monitoring locations set up' });
      break;
    case 'scheduled': {
      phaseIcon = EventIcon;
      const when = startsAt !== null ? formatIn(startsAt, now, t) : '';
      phaseText = when
        ? t('mobile.dashboard.strip.scheduledIn', { defaultValue: 'Scheduled · starts {{when}}', when })
        : t('mobile.dashboard.strip.scheduled', { defaultValue: 'Scheduled' });
      if (startsAt !== null) phaseDetail = shortDateTime(startsAt, timezoneIana);
      break;
    }
    case 'awaiting':
      phaseIcon = HourglassIcon;
      phaseText = clock
        ? t('mobile.dashboard.strip.awaitingSince', { defaultValue: 'Awaiting first reading · started {{ago}}', ago: formatAgo(clock.at, now, t) })
        : t('mobile.dashboard.strip.awaiting', { defaultValue: 'Awaiting first reading' });
      break;
    case 'ended':
      phaseIcon = StopCircleIcon;
      phaseText = endedAt !== null
        ? t('mobile.dashboard.strip.endedAt', { defaultValue: 'Monitoring ended {{at}}', at: shortDateTime(endedAt, timezoneIana) })
        : t('mobile.dashboard.strip.ended', { defaultValue: 'Monitoring ended' });
      break;
    default:
      if (progress) {
        phaseText = t('mobile.dashboard.strip.monitoringProgress', {
          defaultValue: 'Monitoring · {{progress}}',
          progress: progress.charAt(0).toUpperCase() + progress.slice(1),
        });
      } else {
        phaseText = clock
          ? t('mobile.dashboard.strip.monitoringSince', { defaultValue: 'Monitoring · {{since}}', since: sinceText(clock, now, t) })
          : t('mobile.dashboard.strip.monitoring', { defaultValue: 'Monitoring' });
        if (clock) phaseDetail = shortDateTime(clock.at, timezoneIana);
      }
      break;
  }

  const chips: React.ReactNode[] = [];
  if (phase === 'monitoring' && counts) {
    if (counts.hasLimits && counts.outsideLimits > 0) {
      chips.push(
        <CountChip
          key="outside"
          tone="error"
          Icon={ThermostatIcon}
          text={t('mobile.dashboard.strip.outsideLimits', {
            count: counts.outsideLimits,
            defaultValue_one: '{{count}} outside limits',
            defaultValue_other: '{{count}} outside limits',
          })}
        />,
      );
    }
    if (counts.notReporting > 0) {
      chips.push(
        <CountChip
          key="offline"
          tone="error"
          Icon={CloudOffIcon}
          text={t('mobile.dashboard.strip.notReporting', {
            count: counts.notReporting,
            defaultValue_one: '{{count}} not reporting',
            defaultValue_other: '{{count}} not reporting',
          })}
        />,
      );
    }
    if (counts.sensor > 0) {
      chips.push(
        <CountChip
          key="sensor"
          tone="warning"
          Icon={WarningIcon}
          text={t('mobile.dashboard.strip.sensor', {
            count: counts.sensor,
            defaultValue_one: '{{count}} sensor issue',
            defaultValue_other: '{{count}} sensor issues',
          })}
        />,
      );
    }
    if (evaporationOver > 0) {
      chips.push(
        <CountChip
          key="evaporation"
          tone="error"
          Icon={SunIcon}
          text={t('mobile.dashboard.strip.evaporationOverCount', {
            count: evaporationOver,
            defaultValue_one: '{{count}} over evaporation limit',
            defaultValue_other: '{{count}} over evaporation limit',
          })}
        />,
      );
    }
    // Only problems are counted; the cards show everything else.
  }
  if (locationCount && phase === 'monitoring') {
    chips.push(
      <CountChip
        key="locations"
        tone="neutral"
        Icon={SensorsIcon}
        text={t('mobile.dashboard.overview.locationCount', {
          count: locationCount,
          defaultValue_one: '{{count}} location',
          defaultValue_other: '{{count}} locations',
        })}
      />,
    );
  }

  // Its freshness (at the last fetch) in colour and words while monitoring;
  // an ended pour's last reading is old by nature, so plain.
  const newestLevel = newestReadingAt !== null && phase !== 'ended'
    ? getOnlineStatus(newestReadingAt, refNow).level
    : null;
  const newest = newestReadingAt !== null && phase !== 'no_locations' && phase !== 'scheduled'
    ? {
        ago: formatAgo(newestReadingAt, now, t),
        at: shortDateTime(newestReadingAt, timezoneIana),
        color: newestLevel ? FRESHNESS_TINTS[newestLevel].fg : 'text.secondary',
        word: newestLevel === 'delayed' || newestLevel === 'offline' ? freshnessWord(newestLevel, t) : '',
      }
    : null;

  // With the progress in the phase line, the start moves to the newest
  // reading's line: "Concrete start 21 Oct, 07:30".
  const startLine = phase === 'monitoring' && progress && clock
    ? t(`mobile.dashboard.strip.startAt.${clock.kind}`, {
        defaultValue: `${START_WORDS[clock.kind] ?? 'Pour start'} {{at}}`,
        at: shortDateTime(clock.at, timezoneIana),
      })
    : null;
  const withTiles = !!tiles && tiles.length > 0;
  let diffLine: string | null = null;
  if (worstDiff && !withTiles) {
    const parts = [
      worstDiff.over ? t('mobile.dashboard.strip.diffOver', { defaultValue: 'Over limit' }) : null,
      t('mobile.dashboard.strip.worstDiff', { defaultValue: 'Worst differential {{value}} °C', value: worstDiff.value.toFixed(1) }),
      worstDiff.limit !== null
        ? t('mobile.dashboard.strip.diffLimit', { defaultValue: 'limit {{limit}} °C', limit: limitNumber(worstDiff.limit) })
        : null,
      worstDiff.partnerName
        ? t('mobile.dashboard.strip.diffPair', { defaultValue: '{{location}} vs {{partner}}', location: worstDiff.locationName, partner: worstDiff.partnerName })
        : worstDiff.locationName,
    ];
    diffLine = parts.filter(Boolean).join(' · ');
  }

  let maturityLine: string | null = null;
  if (maturity && !withTiles) {
    if (maturity.allReached) {
      maturityLine = t('mobile.dashboard.strip.allTargetsReached', { defaultValue: 'All targets reached' });
    } else {
      const target = [maturity.nextTargetName, maturity.strengthText].filter(Boolean).join(' ')
        || (maturity.nextTargetMaturity !== null && meta ? formatMaturity(maturity.nextTargetMaturity, meta) : '');
      const parts = [
        target ? t('mobile.dashboard.strip.nextTarget', { defaultValue: 'Next: {{target}}', target }) : null,
        t('mobile.dashboard.strip.reachedOf', {
          count: maturity.total,
          reached: maturity.reached,
          defaultValue_one: '{{reached}} of {{count}} location reached',
          defaultValue_other: '{{reached}} of {{count}} locations reached',
        }),
        maturity.lowest && maturity.lowest.strengthText
          ? t('mobile.dashboard.strip.lowest', { defaultValue: 'lowest: {{name}} at {{value}}', name: maturity.lowest.name, value: maturity.lowest.strengthText })
          : null,
      ];
      maturityLine = parts.filter(Boolean).join(' · ');
    }
  }

  let siteLine: string | null = null;
  // With the tiles, the site's temperature and humidity are in the Ambient
  // tile; the evaporation rate keeps its line.
  if (site && (withTiles ? site.evaporation !== null : site.temp !== null || site.humidity !== null || site.evaporation !== null)) {
    const parts = [
      !withTiles && site.temp !== null ? `${site.temp.toFixed(1)} °C` : null,
      !withTiles && site.humidity !== null ? `${site.humidity.toFixed(0)} %RH` : null,
      site.evaporation !== null
        ? t('mobile.dashboard.strip.evaporation', { defaultValue: 'evaporation {{value}} kg/m²/h', value: site.evaporation.toFixed(2) })
        : null,
      // In words, not by the line's colour alone.
      site.evaporationOver ? t('mobile.dashboard.strip.evaporationOver', { defaultValue: 'Over limit' }) : null,
    ];
    siteLine = t('mobile.dashboard.strip.siteNow', { defaultValue: 'Site now {{values}}', values: parts.filter(Boolean).join(' · ') });
  }

  return (
    <Box data-testid="pour-status-strip" sx={{ ...SOFT_CARD_SX, p: 2, mb: 2, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <StripLine Icon={phaseIcon} testId="strip-phase">
          {phaseText}
          {phaseDetail && (
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {` (${phaseDetail})`}
            </Box>
          )}
        </StripLine>
        {chips.length > 0 && (
          <Box data-testid="strip-counts" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {chips}
          </Box>
        )}
        {newest && startLine ? (
          // "Concrete start 21 Oct, 07:30 · newest reading 4 min ago"
          <Typography component="div" data-testid="strip-newest" sx={{ ...LINE_SX, fontSize: '0.8125rem', fontWeight: 400, color: 'text.secondary' }}>
            {`${startLine} · `}
            <Box component="span" sx={{ fontWeight: 600, color: newest.color }}>
              {t('mobile.dashboard.strip.newestReadingLower', { defaultValue: 'newest reading {{ago}}', ago: newest.ago })}
              {newest.word && ` · ${newest.word}`}
            </Box>
          </Typography>
        ) : newest && (
          <Typography component="div" data-testid="strip-newest" sx={{ ...LINE_SX, fontSize: '0.8125rem', color: newest.color }}>
            {t('mobile.dashboard.strip.newestReading', { defaultValue: 'Newest reading {{ago}}', ago: newest.ago })}
            {newest.word && ` · ${newest.word}`}
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>{` (${newest.at})`}</Box>
          </Typography>
        )}
        {diffLine && (
          <StripLine Icon={SwapVertIcon} color={worstDiff?.over ? 'error.main' : undefined} testId="strip-differential">
            {diffLine}
          </StripLine>
        )}
        {maturityLine && (
          <StripLine Icon={maturity?.allReached ? CheckCircleIcon : TargetIcon} testId="strip-maturity">
            {maturityLine}
          </StripLine>
        )}
        {siteLine && (
          <StripLine Icon={SunIcon} color={site?.evaporationOver ? 'error.main' : undefined} testId="strip-site">
            {siteLine}
          </StripLine>
        )}
        {withTiles && (
          <Box sx={{ mt: -0.5 }}>
            <ReadingTiles tiles={tiles as PourRowTile[]} />
            <TargetBar target={target ?? null} />
          </Box>
        )}
        {targetCounts && targetCounts.length > 0 && (
          <Box data-testid="strip-targets" sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25, mt: 0.25 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
              {t('mobile.dashboard.strip.targetsReachedBy', { defaultValue: 'Locations that reached each target' })}
            </Typography>
            {targetCounts.map((tc, i) => {
              const label = [tc.name, tc.strengthText || (meta ? formatMaturity(tc.maturity, meta) : '')].filter(Boolean).join(' ');
              const pct = tc.total > 0 ? Math.round((tc.reached / tc.total) * 100) : 0;
              return (
                <Box
                  key={`${label}-${i}`}
                  data-testid="strip-target"
                  sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 64px auto', alignItems: 'center', gap: 1, py: 0.375 }}
                >
                  <Typography noWrap sx={{ fontSize: '0.8125rem' }}>
                    {label}
                  </Typography>
                  <Box aria-hidden sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'primary.main' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {t('mobile.dashboard.strip.reachedCount', { defaultValue: '{{reached}} of {{total}}', reached: tc.reached, total: tc.total })}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        {fromList && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('mobile.dashboard.strip.fromList', { defaultValue: 'From the Control Panel, updating' })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** The strip's place while nothing is known yet. */
export const PourStatusStripSkeleton: React.FC = () => (
  <Box data-testid="pour-status-strip-skeleton" sx={{ ...SOFT_CARD_SX, p: 2, mb: 2 }}>
    <Skeleton variant="text" width="70%" height={24} />
    <Skeleton variant="text" width="50%" height={32} />
    <Skeleton variant="text" width="40%" height={20} />
  </Box>
);

/** The strip's props from a Control Panel list row (router state), so the
 *  strip paints at once while the dashboard loads. Counts only when the row
 *  carries the phone's health detail. */
export const stripFromListPour = (pour: Pour): Omit<PourStatusStripProps, 'refNow' | 'fromList'> => {
  const ended = pourStatusEnded(pour.PourStatus);
  const newest = toEpochMs(pour.NewestReadingAt);
  const hasDetail = pour.OverLimitCount !== undefined || pour.OfflineCount !== undefined;
  let phase: PourPhase = 'monitoring';
  if (ended) phase = 'ended';
  else if (pour.Scheduled) phase = 'scheduled';
  else if (pour.LoggerCount === 0) phase = 'no_locations';
  else if (newest === null && pour.NewestReadingAt !== undefined) phase = 'awaiting';
  return {
    phase,
    clock: pourClockStart({ started: pour.Started, concreteStart: pour.ConcreteStart, loggerStart: pour.LoggerStart }),
    startsAt: toEpochMs(pour.StartsAt),
    endedAt: toEpochMs(pour.LoggerEnd),
    newestReadingAt: newest,
    counts: hasDetail
      ? {
          outsideLimits: pour.OverLimitCount ?? 0,
          hasLimits: pour.OverLimitCount != null,
          notReporting: pour.OfflineCount ?? 0,
          delayed: 0,
          sensor: pour.WarningCount ?? 0,
          ok: null,
          reporting: null,
          waiting: 0,
        }
      : null,
    timezoneIana: pour.TimeZoneIANA,
  };
};

export default PourStatusStrip;
